import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, ShieldCheck, Users, Layers, Megaphone, Share2, Trash2, ExternalLink, CheckCircle2, Copy, Radio } from "lucide-react";
import { api } from "@/api/client";
import type { CohortSnapshotData, CohortSnapshotMember, DashboardBrandSeries, FeedItem, MetricPoint, PulseGroup, PulsePromptRow, ResolvedTopicWithEstimate, SnapshotReportSummary, TopicOverviewData, TopicSnapshotData, TopicSnapshotEntry, TrackingPlan } from "../../../types";
import { useTopicsStore } from "@/stores/topics";
import { useDefaultModelSeed, useModelsStore } from "@/stores/models";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { ErrorState } from "@/components/ui/error-state";
import { EmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";
import { formatTimesPerDay } from "@/lib/format";
import { TrendChart } from "@/components/dashboard/TrendChart";
import { SentimentExplanationPanel } from "@/components/dashboard/SentimentExplanationPanel";
import { HealthPill, MovementGlyph, SentimentDelta, ShowMoreButton, pinnedCollapse, sentimentBand } from "@/components/topics/shared";

const MONTHS = 6;
const GAP_THRESHOLD = 0.5; // below this, a gap (or gap trend) reads as even/flat
const FIELD_LIMIT = 8;     // objectivity list rows before "Show all" (primary pinned)
const SOV_LIMIT = 8;       // share-of-voice rows before "Show all" (primary pinned)

function num(n: number | null | undefined, digits = 0): string {
  return n === null || n === undefined ? "—" : n.toFixed(digits);
}

/** 'YYYY-MM-DD HH:MM:SS' (UTC) → "Mon, Jul 6". */
function fmtSqlDate(sql: string): string {
  return new Date(sql.replace(" ", "T") + "Z").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/** Compact feed timestamps: "today", a weekday within the week, else "Jun 24". */
function fmtFeedDate(sql: string): string {
  const d = new Date(sql.replace(" ", "T") + "Z");
  const daysAgo = (Date.now() - d.getTime()) / 86_400_000;
  if (daysAgo < 1) return "today";
  if (daysAgo < 7) return d.toLocaleDateString(undefined, { weekday: "short" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Trend line — shape only, no axes. Filled area under the line like the prototype. */
function Sparkline({ series, warn, width = 64, height = 24, take = 8 }: { series: MetricPoint[]; warn: boolean; width?: number; height?: number; take?: number }) {
  const pts = series.slice(-take).map((p) => p.avg);
  if (pts.length < 2) return null;
  const min = Math.min(...pts);
  const span = Math.max(Math.max(...pts) - min, 1);
  const padY = 3;
  const innerW = width - 4;
  const coords = pts.map((v, i) => ({
    x: 2 + (i * innerW) / (pts.length - 1),
    y: padY + (1 - (v - min) / span) * (height - 2 * padY),
  }));
  const last = coords[coords.length - 1]!;
  const first = coords[0]!;
  const color = warn ? "#dc2626" : "#6366f1";
  const line = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const area = `${coords.map((c, i) => `${i ? "L" : "M"}${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ")} L${last.x.toFixed(1)} ${height} L${first.x.toFixed(1)} ${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" aria-hidden="true" className="mt-1">
      <path d={area} fill={color} opacity={0.1} />
      <polyline
        points={line}
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={warn ? 0.75 : 1}
      />
      <circle cx={last.x.toFixed(1)} cy={last.y.toFixed(1)} r="2.4" fill={color} />
    </svg>
  );
}

/** Colored tracking/health dot, banded by the topic's field-sentiment value. */
function BandDot({ value }: { value: number | null | undefined }) {
  const band = sentimentBand(value);
  return (
    <span
      className={cn(
        "w-[7px] h-[7px] rounded-full shrink-0",
        band === "good" && "bg-green-600",
        band === "mid" && "bg-accent-500",
        band === "low" && "bg-red-500",
        band === "pending" && "bg-slate-300",
      )}
      aria-hidden="true"
    />
  );
}

function TopicCard({ topic }: { topic: TopicSnapshotEntry }) {
  const p = topic.primary;
  const delta = p?.sentiment_delta_month ?? null;
  const warn = delta !== null && delta < -GAP_THRESHOLD;
  const label = topic.category.charAt(0).toUpperCase() + topic.category.slice(1);
  // Blind leads — "blind is the heart of the topic": the AI-recommendation rank
  // is the first stat line, field sentiment sits below it with its own sparkline.
  const blind = topic.blind;
  const deltaDir = delta === null ? "flat" : delta > 0.05 ? "up" : delta < -0.05 ? "down" : "flat";
  const mover = topic.top_mover;
  return (
    <Link to={`/app/topics/${topic.topic_id}`} aria-label={`Open ${label} topic space`}>
      <Card className="h-full cursor-pointer transition-colors hover:border-brand-600">
        <CardContent className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <BandDot value={p?.sentiment_latest} />
            <span className="text-[15px] font-semibold text-brand-950 truncate">{label}</span>
            <HealthPill value={p?.sentiment_latest} className="ml-auto" />
          </div>

          <div className="text-[12px] text-slate-500">
            AI recommendations{" "}
            <b className="font-bold text-brand-950 tabular-nums">
              {blind && blind.rank !== null ? `#${blind.rank} of ${blind.field_size}` : `— of ${blind?.field_size ?? topic.field_size}`}
            </b>{" "}
            {blind && <MovementGlyph rank={blind.rank} prevRank={blind.prev_rank} />}{" "}
            <span className="text-slate-400">· blind</span>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">Field sentiment</div>
              <span className="text-[16px] font-semibold text-brand-950 tabular-nums">{num(p?.sentiment_latest, 1)}</span>{" "}
              {delta !== null && (
                <span
                  className={cn(
                    "text-[12px] font-semibold tabular-nums",
                    deltaDir === "up" && "text-green-600",
                    deltaDir === "down" && "text-red-500",
                    deltaDir === "flat" && "text-slate-400",
                  )}
                >
                  {deltaDir === "up" ? "▲ +" : deltaDir === "down" ? "▼ " : ""}{delta.toFixed(1)} MoM
                </span>
              )}
            </div>
            {p && <Sparkline series={p.series} warn={warn} />}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-2.5 text-[12px] text-slate-500">
            <span className="truncate">
              {mover ? (
                <>Top mover: <span className="font-semibold text-slate-700">{mover.name} {mover.delta > 0 ? "+" : ""}{mover.delta.toFixed(1)} visibility</span></>
              ) : (
                <>{topic.prompt_count} prompt{topic.prompt_count === 1 ? "" : "s"} tracked</>
              )}
            </span>
            <span className="font-semibold text-brand-600 shrink-0">Open →</span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

/** One pulse prompt row: name, latest avg, and a bare signed delta. */
function PulseRow({ pp }: { pp: PulsePromptRow }) {
  return (
    <div className="flex items-center gap-3 py-[7px] border-t border-slate-100 first:border-t-0 text-[12.5px]">
      <span className="flex-1 min-w-0 truncate text-slate-600">{pp.prompt_name}</span>
      <span className="w-10 shrink-0 text-right font-semibold text-slate-700 tabular-nums">{num(pp.avg_sentiment, 1)}</span>
      <span className="w-11 shrink-0 text-right">
        <SentimentDelta delta={pp.delta} glyph={false} className="text-[12px]" />
      </span>
    </div>
  );
}

/** The grouped pulse prompt list. Named subtopic groups get a header (name +
 *  count + avg); when the only group is the null "Other" catch-all we render a
 *  flat list with no header (matches the prototype's ungrouped case). */
function PulseGroups({ groups }: { groups: PulseGroup[] }) {
  const hasNamedGroups = groups.some((g) => g.subtopic_id !== null);
  return (
    <div>
      {groups.map((g) => (
        <div key={g.subtopic_id ?? "other"}>
          {hasNamedGroups && (
            <div className="flex items-center justify-between gap-2 pt-3 pb-0.5">
              <span className="text-[11.5px] font-semibold text-slate-600">{g.name}</span>
              <span className="text-[11px] text-slate-400 tabular-nums">
                {g.prompts.length} prompt{g.prompts.length === 1 ? "" : "s"} · avg {num(g.avg_sentiment, 1)}
              </span>
            </div>
          )}
          {g.prompts.map((pp) => <PulseRow key={pp.prompt_id} pp={pp} />)}
        </div>
      ))}
    </div>
  );
}

const PULSE_CADENCES: { value: string; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "times_per_day", label: "Several times a day" },
];

/**
 * Collapsible "Tracking settings" for the pulse topic — the tracking form's
 * cadence + model controls, scoped to the pulse (field on, blind off). Saves via
 * api.topics.setTracking and re-pulls the topic list + overview + resolved topic.
 * Other schedule fields (hour/day) are preserved from the current config.
 */
function PulseTrackingSettings({ topicId, resolved, onSaved }: {
  topicId: number;
  resolved: ResolvedTopicWithEstimate | null;
  onSaved: () => Promise<void>;
}) {
  const tr = resolved?.tracking ?? null;
  const catalog = useModelsStore((s) => s.models);
  const fetchModels = useModelsStore((s) => s.fetch);

  const [cadence, setCadence] = useState<string>(tr?.cadence || "weekly");
  const [timesPerDay, setTimesPerDay] = useState(tr?.times_per_day ?? 4);
  const [models, setModels] = useState<Set<string>>(new Set(tr?.models ?? []));
  const [estimate, setEstimate] = useState<number | null>(resolved?.estimate.estimated_calls_per_month ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { fetchModels(); }, [fetchModels]);
  useDefaultModelSeed(models.size === 0, (ids) => setModels(new Set(ids)));

  // Live cost preview — decorative, silent on failure. Debounced ~400ms.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      api.topics.estimate(topicId, {
        models: [...models],
        cadence,
        times_per_day: cadence === "times_per_day" ? timesPerDay : undefined,
        field_enabled: true,
        blind_enabled: false,
      })
        .then((e) => { if (!cancelled) setEstimate(e.estimated_calls_per_month); })
        .catch(() => {});
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [topicId, models, cadence, timesPerDay]);

  const toggleModel = (id: string) => {
    setModels((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.topics.setTracking(topicId, {
        enabled: tr?.enabled ?? true,
        field_enabled: true,
        blind_enabled: false,
        cadence,
        hour_utc: tr?.hour_utc ?? 9,
        day_of_week: tr?.day_of_week ?? 1,
        day_of_month: tr?.day_of_month ?? 1,
        times_per_day: cadence === "times_per_day" ? timesPerDay : null,
        models: [...models],
      });
      await onSaved();
    } catch (e: any) {
      setError(e?.message || "Could not save tracking.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-3.5 space-y-3">
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="pulse-cadence">Cadence</Label>
          <NativeSelect id="pulse-cadence" value={cadence} onChange={(e) => setCadence(e.target.value)} className="w-48">
            {PULSE_CADENCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </NativeSelect>
        </div>
        {cadence === "times_per_day" && (
          <div className="space-y-1.5">
            <Label htmlFor="pulse-tpd">Runs per day</Label>
            <NativeSelect id="pulse-tpd" value={timesPerDay} onChange={(e) => setTimesPerDay(parseInt(e.target.value))} className="w-48">
              {Array.from({ length: 23 }, (_, i) => i + 2).map((n) => <option key={n} value={n}>{formatTimesPerDay(n)}</option>)}
            </NativeSelect>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Models</Label>
          <div className="flex flex-wrap gap-1.5">
            {catalog.map((m) => {
              const on = models.has(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleModel(m.id)}
                  aria-pressed={on}
                  className={cn(
                    "px-2.5 py-1 rounded-md text-[12.5px] font-medium border transition-colors",
                    on ? "bg-brand-50 text-brand-600 border-brand-100" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50",
                  )}
                >
                  {m.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12px] text-slate-500">
          {estimate !== null && (
            <>Estimated cost: <b className="font-bold text-brand-600 tabular-nums">~{estimate} AI calls / month</b> · </>
          )}
          {models.size} model{models.size === 1 ? "" : "s"}, no competitor scoring.
        </p>
        <Button size="sm" onClick={save} disabled={saving || models.size === 0}>
          {saving ? "Saving…" : "Save tracking"}
        </Button>
      </div>
      {error && <p className="text-[12.5px] text-red-600">{error}</p>}
    </div>
  );
}

/**
 * Brand pulse: how AI talks about the primary brand on its own (no competitors),
 * tracked via a dedicated pulse-kind topic. Renders a CTA when none exists, or —
 * once runs land — the prototype's two-column pulse card (stat + grouped prompt
 * list) with an expandable tracking settings panel. The fetches are decorative:
 * failures fall back to the "no data yet" copy rather than breaking Brand Health.
 */
function BrandHealthPulse({ primaryName }: { primaryName: string }) {
  const navigate = useNavigate();
  const topics = useTopicsStore((s) => s.topics);
  const topicsLoading = useTopicsStore((s) => s.loading);
  const fetchTopics = useTopicsStore((s) => s.fetch);
  const refreshTopics = useTopicsStore((s) => s.refresh);

  const pulseTopic = topics.find((t) => t.kind === "pulse") ?? null;
  const [overview, setOverview] = useState<TopicOverviewData | null>(null);
  const [resolved, setResolved] = useState<ResolvedTopicWithEstimate | null>(null);
  const [creating, setCreating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // The sidebar owns the initial topics fetch, but Brand Health can be the first
  // paint on a hard load — pull the list if it hasn't arrived yet.
  useEffect(() => { if (topics.length === 0 && !topicsLoading) fetchTopics(); }, []);

  const loadPulse = () => {
    if (!pulseTopic) { setOverview(null); setResolved(null); return () => {}; }
    let cancelled = false;
    api.topics.overview(pulseTopic.id).then((o) => { if (!cancelled) setOverview(o); }).catch(() => {});
    api.topics.get(pulseTopic.id).then((r) => { if (!cancelled) setResolved(r); }).catch(() => {});
    return () => { cancelled = true; };
  };
  useEffect(loadPulse, [pulseTopic?.id]);

  const createPulse = async () => {
    setCreating(true);
    try {
      const topic = await api.topics.create({ kind: "pulse", name: "Brand pulse" });
      await refreshTopics();
      navigate(`/app/topics/${topic.id}?tab=prompts`);
    } catch {
      setCreating(false);
    }
  };

  // Refetch everything the pulse card reads after a tracking save.
  const onSaved = async () => {
    await refreshTopics();
    if (pulseTopic) {
      await Promise.all([
        api.topics.overview(pulseTopic.id).then(setOverview).catch(() => {}),
        api.topics.get(pulseTopic.id).then(setResolved).catch(() => {}),
      ]);
    }
  };

  // Still loading the topics list — don't flash the CTA before we know.
  if (!pulseTopic && topicsLoading && topics.length === 0) return null;

  if (!pulseTopic) {
    return (
      <Card className="border-l-4 border-l-brand-400 bg-brand-50/30">
        <CardContent className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Radio className="w-4 h-4 text-brand-500 mt-0.5 shrink-0" />
            <div>
              <div className="text-[13.5px] font-bold text-brand-950">Brand pulse</div>
              <p className="text-[12.5px] text-slate-500">Track how AI talks about {primaryName} on its own — no competitors.</p>
            </div>
          </div>
          <Button onClick={createPulse} disabled={creating}>
            {creating ? "Setting up…" : "Set up brand pulse"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const pulse = overview?.pulse ?? null;
  const current = pulse?.summary.current.avg ?? null;
  const delta = pulse?.summary.delta ?? null;
  const warn = delta !== null && delta < -GAP_THRESHOLD;
  const promptCount = pulse?.per_prompt.length ?? 0;
  const modelCount = resolved?.tracking?.models.length ?? 0;
  const cadenceLabel = resolved?.tracking?.cadence || "weekly";

  return (
    <Card className="border-l-4 border-l-brand-500">
      <CardContent className="space-y-3">
        {/* Card head — sublabel, tracking chip, settings toggle, manage link */}
        <div className="flex items-center gap-2 flex-wrap">
          <Radio className="w-4 h-4 text-brand-500 shrink-0" />
          <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">{primaryName} alone · no competitor field</span>
          {pulseTopic.tracking_enabled && (
            <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-[11.5px] font-semibold text-brand-600 tabular-nums">
              Tracking {cadenceLabel} · {modelCount} model{modelCount === 1 ? "" : "s"}
            </span>
          )}
          <div className="ml-auto flex items-center gap-3 shrink-0">
            {current !== null && (
              <button
                type="button"
                onClick={() => setSettingsOpen((v) => !v)}
                aria-expanded={settingsOpen}
                className="text-[12px] font-medium text-slate-500 hover:text-brand-600"
              >
                Tracking settings
              </button>
            )}
            <Link to={`/app/topics/${pulseTopic.id}`} className="text-[12px] font-medium text-brand-600 hover:underline">Manage pulse →</Link>
          </div>
        </div>

        {current === null ? (
          <p className="text-[12.5px] text-slate-500">
            {pulseTopic.tracking_enabled ? "Tracking scheduled — the first pulse lands after the next run." : "No runs yet — "}
            {!pulseTopic.tracking_enabled && <Link to={`/app/topics/${pulseTopic.id}?tab=tracking`} className="text-brand-600 font-medium hover:underline">set up tracking →</Link>}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-x-9 gap-y-3 md:grid-cols-[240px_minmax(0,1fr)]">
              {/* Left: pulse stat + delta + sparkline + note */}
              <div>
                <div className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">Pulse sentiment</div>
                <div className="mt-1.5 flex items-end gap-2">
                  <span className="text-[34px] font-bold leading-none text-brand-700 tabular-nums">{num(current, 1)}</span>
                  <SentimentDelta delta={delta} suffix=" vs last week" className="mb-1 text-[12px]" />
                </div>
                {pulse && pulse.series.length > 1 && <Sparkline series={pulse.series} warn={warn} width={220} height={44} take={pulse.series.length} />}
                <p className="mt-2.5 text-[12px] text-slate-500 leading-relaxed">
                  Reputation, trust and quality questions about {primaryName} itself. Arenas compare — the pulse listens.
                </p>
              </div>
              {/* Right: grouped prompt list */}
              <div>
                <div className="flex items-center justify-between gap-2 pb-0.5">
                  <span className="text-[11.5px] font-semibold text-slate-600 tabular-nums">
                    {promptCount} pulse prompt{promptCount === 1 ? "" : "s"}
                    {pulse && pulse.groups.some((g) => g.subtopic_id !== null) && ` · ${pulse.groups.filter((g) => g.subtopic_id !== null).length} group${pulse.groups.filter((g) => g.subtopic_id !== null).length === 1 ? "" : "s"}`}
                  </span>
                  <Link to={`/app/topics/${pulseTopic.id}?tab=prompts`} className="text-[12px] font-medium text-brand-600 hover:underline shrink-0">Manage prompts →</Link>
                </div>
                {pulse && pulse.groups.length > 0 && <PulseGroups groups={pulse.groups} />}
              </div>
            </div>
            {settingsOpen && (
              <PulseTrackingSettings topicId={pulseTopic.id} resolved={resolved} onSaved={onSaved} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function SnapshotView() {
  const [data, setData] = useState<CohortSnapshotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [plan, setPlan] = useState<TrackingPlan | null>(null);
  const [topics, setTopics] = useState<TopicSnapshotData | null>(null);
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [reports, setReports] = useState<SnapshotReportSummary[]>([]);
  const [reportsMsg, setReportsMsg] = useState("");
  const [creatingReport, setCreatingReport] = useState(false);
  const [lastReport, setLastReport] = useState<{ name: string; path: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showAllSov, setShowAllSov] = useState(false);
  const [showAllField, setShowAllField] = useState(false);
  const allTopics = useTopicsStore((s) => s.topics);

  const createReport = async () => {
    setCreatingReport(true);
    setReportsMsg("");
    try {
      // Freeze exactly what's on screen, including the lens the view is pinned to.
      const r = await api.snapshots.create({
        months: MONTHS,
        bucket: data?.bucket === "week" || data?.bucket === "month" ? data.bucket : "week",
        prompt_id: data?.lens.prompt_id ?? undefined,
        prompt_ids: data?.lens.prompt_ids ?? undefined,
      });
      setReports((prev) => [r, ...prev]);
      // No window.open here — after an await, popup blockers eat the tab.
      setLastReport({ name: r.name, path: r.public_path });
      setCopied(false);
    } catch (e: any) {
      setReportsMsg(e?.message || "Could not create report.");
    } finally {
      setCreatingReport(false);
    }
  };

  const copyReportLink = async () => {
    if (!lastReport) return;
    try {
      await navigator.clipboard.writeText(window.location.origin + lastReport.path);
      setCopied(true);
    } catch {
      setReportsMsg("Could not copy — open the report and copy the address instead.");
    }
  };

  const deleteReport = async (id: number) => {
    if (!window.confirm("Delete this report? Anyone holding the link will lose access.")) return;
    const target = reports.find((r) => r.id === id);
    try {
      await api.snapshots.delete(id);
      setReports((prev) => prev.filter((r) => r.id !== id));
      if (target && lastReport?.path === `/snapshots/${target.slug}`) setLastReport(null);
    } catch (e: any) {
      setReportsMsg(e?.message || "Could not delete report.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      // Plan first: when the tracking plan is active, the whole view pins to its
      // prompt set so every figure shares one lens.
      let currentPlan: TrackingPlan | null = null;
      try { currentPlan = await api.plan.get(); } catch { /* the plan is an enhancement, not a gate */ }
      if (cancelled) return;
      setPlan(currentPlan);
      const lensIds = currentPlan?.active && currentPlan.prompt_ids.length > 0 ? currentPlan.prompt_ids : undefined;
      let snap = await api.metrics.cohortSnapshot({ metric: "sentiment", months: MONTHS, bucket: "week", prompt_ids: lensIds });
      if (lensIds && !snap.has_data && !cancelled) {
        // The plan is on but hasn't produced runs yet — show whatever history
        // exists unpinned rather than an empty page. lens in `snap` stays honest.
        snap = await api.metrics.cohortSnapshot({ metric: "sentiment", months: MONTHS, bucket: "week" });
      }
      if (!cancelled) { setData(snap); setError(""); }
    })()
      .catch((err) => { if (!cancelled) setError(err?.message || "Failed to load snapshot"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  useEffect(() => {
    let cancelled = false;
    api.snapshots.list()
      .then((r) => { if (!cancelled) setReports(r); })
      .catch(() => { if (!cancelled) setReportsMsg("Could not load shared reports."); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Topic cards are an enhancement — the page renders without them. Not pinned
  // to the tracking prompt: each topic is its own lens (same topic prompts for
  // every brand), which is what makes the per-topic ranks comparable.
  useEffect(() => {
    let cancelled = false;
    api.metrics.topicSnapshot({ months: MONTHS })
      .then((t) => { if (!cancelled) setTopics(t); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Same for the "what changed" feed — decoration, never a gate.
  useEffect(() => {
    let cancelled = false;
    api.feed.recent({ days: 7 })
      .then((f) => { if (!cancelled) setFeedItems(f); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [reloadKey]);

  if (loading && !data) return <Spinner message="Loading snapshot…" size="lg" />;
  if (error && !data) return <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />;
  if (!data) return null;

  if (!data.primary) {
    return (
      <EmptyState
        icon={Layers}
        title="Set a primary brand"
        description="Brand Health benchmarks one primary brand against the competitors it's tracked with. Pick a primary brand for this workspace, then add the competitors to compare against."
        action={<Link to="/app/brands"><Button>Go to Brands</Button></Link>}
      />
    );
  }

  const planNextRun = plan?.active
    ? plan.topics.map((t) => t.next_run_at).filter((d): d is string => !!d).sort()[0] ?? null
    : null;

  // Hero honesty sublabel — what this index rolls up, counted from the topics
  // store (topic reality), not the legacy plan payload. Each segment is omitted
  // when its data is absent; the whole line disappears when nothing is present.
  // The next-run date still comes from the plan (managed schedules — correct
  // post-adoption).
  const arenaTopicCount = allTopics.filter((t) => t.kind === "arena" && !t.archived_at && t.prompt_count > 0).length;
  const hasPulse = allTopics.some((t) => t.kind === "pulse");
  const trackingTopicCount = allTopics.filter((t) => t.tracking_enabled).length;
  const trackedPromptCount = allTopics.filter((t) => t.tracking_enabled).reduce((sum, t) => sum + t.prompt_count, 0);
  const heroSegments = [
    arenaTopicCount > 0 ? `Rolls up ${arenaTopicCount} topic space${arenaTopicCount === 1 ? "" : "s"}` : null,
    trackedPromptCount > 0 ? `${trackedPromptCount} tracked prompt${trackedPromptCount === 1 ? "" : "s"}` : null,
    hasPulse ? "brand pulse" : null,
    planNextRun ? `Next run ${fmtSqlDate(planNextRun)}` : null,
  ].filter((s): s is string => s !== null);

  if (!data.has_data) {
    return (
      <EmptyState
        icon={Layers}
        title={plan?.active ? "Tracking scheduled — data on the way" : "No sentiment history yet"}
        description={plan?.active
          ? `Tracking is set up${planNextRun ? ` — the first data lands after the next run on ${fmtSqlDate(planNextRun)}` : ""}. You can also run a tracked study immediately from Studies.`
          : "Set up a topic space to start collecting month-over-month sentiment for your primary brand and its competitors — or set up a study manually."}
        action={
          <div className="flex items-center gap-3">
            {plan?.active ? (
              <Link to="/app/topics/new"><Button>New topic space</Button></Link>
            ) : (
              <>
                <Link to="/app/topics/new"><Button>Set up tracking</Button></Link>
                <Link to="/app/studies"><Button variant="secondary">Set up manually</Button></Link>
              </>
            )}
          </div>
        }
      />
    );
  }

  const { primary } = data;
  const pinnedSingle = data.lens.prompt_id !== null;
  const pinnedLens = pinnedSingle || (data.lens.prompt_ids?.length ?? 0) > 0;
  const deltaDir = primary.delta_month === null ? "none" : primary.delta_month > 0.05 ? "up" : primary.delta_month < -0.05 ? "down" : "flat";
  const DeltaIcon = deltaDir === "up" ? TrendingUp : deltaDir === "down" ? TrendingDown : Minus;

  // ONE us-vs-them overlay: the primary (hero) plus each individual competitor,
  // deduped in cohort order. This replaced both the cohort-median comparative
  // chart and the per-competitor small-multiple grid — the individual series are
  // the same data at full resolution, and the median they rolled up survives as
  // the chart's reference line below. Falls back to the cohort medians only when
  // no member series exist (legacy snapshots that stored medians alone).
  const primarySeries: DashboardBrandSeries = {
    brand_id: primary.brand_id,
    brand_name: primary.brand_name,
    is_primary: true,
    latest: primary.latest,
    deltas: { day: null, week: null, month: primary.delta_month },
    series: primary.series,
  };
  const memberAsSeries = (m: CohortSnapshotMember): DashboardBrandSeries => ({
    brand_id: m.brand_id,
    brand_name: m.brand_name,
    is_primary: false,
    latest: m.latest,
    deltas: { day: null, week: null, month: null },
    series: m.series,
  });
  const seenCompetitors = new Set<number>();
  const competitorSeries: DashboardBrandSeries[] = [];
  for (const c of data.cohorts) {
    for (const m of c.members ?? []) {
      if (seenCompetitors.has(m.brand_id) || m.series.length === 0) continue;
      seenCompetitors.add(m.brand_id);
      competitorSeries.push(memberAsSeries(m));
    }
  }
  const cohortMedianSeries: DashboardBrandSeries[] = data.cohorts.map((c) => ({
    brand_id: -c.cohort_id,
    brand_name: `${c.name} · median`,
    is_primary: false,
    latest: c.latest_median,
    deltas: { day: null, week: null, month: null },
    series: c.median_series.map((p) => ({ bucket: p.bucket, avg: p.value, min: p.value, max: p.value, count: p.count })),
  }));
  const chartBrands: DashboardBrandSeries[] = [
    primarySeries,
    ...(competitorSeries.length > 0 ? competitorSeries : cohortMedianSeries),
  ];

  // Field median per bucket across the plotted competitors — the reference line
  // that carries what the retired cohort-median chart used to show.
  const chartBuckets = Array.from(new Set(chartBrands.flatMap((b) => b.series.map((p) => p.bucket)))).sort();
  const overlayMedian = competitorSeries.length > 0
    ? {
      label: "Field median",
      points: chartBuckets.map((bucket) => {
        const vals = competitorSeries
          .map((s) => s.series.find((p) => p.bucket === bucket)?.avg)
          .filter((v): v is number => v !== undefined && v !== null)
          .sort((a, b) => a - b);
        if (vals.length === 0) return null;
        const mid = Math.floor(vals.length / 2);
        return vals.length % 2 === 0 ? (vals[mid - 1]! + vals[mid]!) / 2 : vals[mid]!;
      }),
    }
    : undefined;

  const sov = data.share_of_voice.filter((e) => e.share > 0);
  const topShare = sov.length > 0 ? Math.max(...sov.map((e) => e.share)) : 0;
  const sovCollapsed = pinnedCollapse(sov, SOV_LIMIT, (e) => e.brand_id === primary.brand_id);
  const fieldCollapsed = pinnedCollapse(data.field, FIELD_LIMIT, (f) => f.is_primary);
  // Field median — the middle competitor's latest sentiment — for the hero's
  // inline "field median X" comparison. null when there are no competitor values.
  const fieldLatest = data.field.filter((f) => !f.is_primary && f.latest !== null).map((f) => f.latest as number).sort((a, b) => a - b);
  const fieldMedian = fieldLatest.length === 0
    ? null
    : fieldLatest.length % 2 === 0
      ? (fieldLatest[fieldLatest.length / 2 - 1]! + fieldLatest[fieldLatest.length / 2]!) / 2
      : fieldLatest[(fieldLatest.length - 1) / 2]!;
  const weekCount = primary.series.length;
  // A lone "general" topic just mirrors the hero — only show the row when
  // there's a real per-topic story to tell.
  const topicCards = topics?.topics ?? [];
  const showTopics = topicCards.length > 0 && !(topicCards.length === 1 && topicCards[0]!.category === "general");

  const renderSovRow = (e: (typeof sov)[number]) => (
    <div key={e.brand_name} className="grid grid-cols-[minmax(110px,1fr)_2fr_auto] items-center gap-3">
      <span className={cn("text-[13px] truncate", e.tracked ? "font-semibold text-brand-950" : "text-slate-600")}>
        {e.brand_name}
      </span>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-brand-500"
          style={{ width: `${topShare > 0 ? (e.share / topShare) * 100 : 0}%` }}
        />
      </div>
      <span className="text-[12.5px] font-semibold tabular-nums text-slate-600 text-right">{e.share.toFixed(0)}%</span>
    </div>
  );

  const renderFieldRow = (f: (typeof data.field)[number]) => (
    <div
      key={f.brand_id}
      className={cn(
        "flex items-center gap-3 rounded-md px-2.5 py-1.5",
        f.is_primary && "bg-brand-50",
      )}
    >
      <span className={cn("w-7 text-[13px] font-bold tabular-nums", f.is_primary ? "text-brand-600" : "text-slate-400")}>
        {f.rank === null ? "—" : `#${f.rank}`}
      </span>
      <span className="flex items-center gap-2 min-w-0 flex-1">
        <Users className="w-3.5 h-3.5 text-slate-300 shrink-0" />
        <span className={cn("text-[13px] truncate", f.is_primary ? "font-semibold text-brand-950" : "text-slate-700")}>
          {f.brand_name}
        </span>
        {f.is_primary && <Badge variant="muted">You</Badge>}
      </span>
      <span className="text-[13px] font-semibold tabular-nums text-slate-600">{num(f.latest)}</span>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[13px] text-slate-500">
          {primary.brand_name} · last {data.months} months
        </p>
        <div className="flex items-center gap-3">
          {/* Chip counts tracking-enabled topics (topic reality, not the legacy
              plan); when nothing is tracking it falls back to the set-up CTA. */}
          {trackingTopicCount > 0 && trackedPromptCount > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-green-700">
              <CheckCircle2 className="w-4 h-4" />
              Tracking {trackingTopicCount} topic{trackingTopicCount === 1 ? "" : "s"}
              {planNextRun && (
                <span className="text-slate-400 font-normal">· next run {fmtSqlDate(planNextRun)}</span>
              )}
            </span>
          ) : (
            <Link to="/app/topics/new"><Button variant="secondary">Set up tracking</Button></Link>
          )}
          <Button onClick={createReport} disabled={creatingReport}>
            <Share2 className="w-4 h-4 mr-1.5" />
            {creatingReport ? "Creating…" : "Create shareable report"}
          </Button>
        </div>
      </div>

      {lastReport && (
        <div className="flex items-center gap-3 rounded-lg border border-brand-100 bg-brand-50/50 px-3.5 py-2.5 text-[13px]">
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          <span className="text-slate-700 truncate">Report ready: <span className="font-semibold">{lastReport.name}</span></span>
          <a href={lastReport.path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline shrink-0">
            <ExternalLink className="w-3.5 h-3.5" /> Open
          </a>
          <button onClick={copyReportLink} className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline shrink-0">
            <Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
      )}
      {reportsMsg && <p className="text-[12.5px] text-red-600">{reportsMsg}</p>}

      {/* Hero sentiment index */}
      <div className="stagger-children">
        <Card>
          <CardContent>
            <div className="flex flex-wrap items-end gap-7">
              <div className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">
                  {primary.brand_name} sentiment index · overall
                </span>
                <span className="text-[44px] font-bold leading-none tracking-tight text-brand-950 tabular-nums">
                  {num(primary.latest, 1)}
                </span>
                <div className="flex items-center gap-2 text-[12px]">
                  <span
                    className={cn(
                      "font-semibold tabular-nums",
                      deltaDir === "up" && "text-green-600",
                      deltaDir === "down" && "text-red-500",
                      (deltaDir === "flat" || deltaDir === "none") && "text-slate-400",
                    )}
                  >
                    {deltaDir === "up" ? "▲ +" : deltaDir === "down" ? "▼ " : ""}
                    {primary.delta_month === null ? "—" : `${primary.delta_month.toFixed(1)} MoM`}
                  </span>
                  {fieldMedian !== null && (
                    <span className="text-slate-400">· field median {fieldMedian.toFixed(1)}</span>
                  )}
                </div>
              </div>
              {weekCount > 1 && (
                <div className="ml-auto flex flex-col items-end gap-1">
                  <Sparkline series={primary.series} warn={deltaDir === "down"} width={220} height={52} take={weekCount} />
                  <span className="text-[11.5px] text-slate-400 tabular-nums">{weekCount} weeks</span>
                </div>
              )}
            </div>
            <p className="mt-3 text-[12.5px] text-slate-500">
              <span className="font-semibold text-slate-600">50 = neutral</span>. Measured by asking AI models about{" "}
              {primary.brand_name} directly, then scoring the tone of the answer.
            </p>
            {heroSegments.length > 0 && (
              <div className="mt-3.5 flex flex-wrap gap-x-5 gap-y-1.5 border-t border-slate-100 pt-3 text-[12px] text-slate-500 tabular-nums">
                {heroSegments.map((s) => <span key={s}>{s}</span>)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Brand pulse — self-monitoring (no competitors) */}
      <div className="space-y-3">
        <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-brand-950">Brand pulse</h2>
        <BrandHealthPulse primaryName={primary.brand_name} />
      </div>

      {/* Topic spaces — how the primary ranks in each tracked area */}
      {showTopics && (
        <div className="space-y-3">
          <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-brand-950">Topic spaces</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 stagger-children">
            {topicCards.slice(0, 9).map((t) => <TopicCard key={t.category} topic={t} />)}
          </div>
          {topicCards.length > 9 && (
            <p className="mt-2 text-[11.5px] text-slate-400">+{topicCards.length - 9} more topics tracked</p>
          )}
        </div>
      )}

      {/* One cohesive us-vs-them overlay (replaces the cohort-median chart and
          the per-competitor small-multiple grid) */}
      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">
              {primary.brand_name} vs competitors — sentiment
            </span>
            <span className="text-[11px] text-slate-400">
              Same lens · {data.bucket === "week" ? "weekly" : "monthly"} · last {data.months} months
            </span>
          </div>
          <TrendChart
            brands={chartBrands}
            bucket={data.bucket}
            heroBrandId={primary.brand_id}
            medianSeries={overlayMedian}
            title={`${primary.brand_name} vs competitors — sentiment`}
            caption={`Same lens · ${data.bucket === "week" ? "weekly" : "monthly"} · last ${data.months} months`}
            exportTopic="brand-health"
            exportChart="sentiment"
          />
          <div className="flex justify-end mt-2">
            <Button variant="ghost" size="sm" onClick={() => setShowExplanation((v) => !v)}>
              {showExplanation ? "Hide score explanation" : "Why this score?"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Positive/negative evidence behind the sentiment score — stored data only */}
      {showExplanation && (
        <SentimentExplanationPanel
          brandId={primary.brand_id}
          brandName={primary.brand_name}
          rangeKey="90d"
          promptIds={data.lens.prompt_ids ?? undefined}
          competitorOptions={data.field
            .filter((f) => !f.is_primary)
            .map((f) => ({ brandId: f.brand_id, brandName: f.brand_name }))}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Share of voice (blind-query data only — see cohortSnapshot) */}
        {sov.length > 0 && (
          <Card className={cn(feedItems.length === 0 && "lg:col-span-2")}>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-brand-500" />
                <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Share of voice — who AI surfaces</span>
              </div>
              <div className="space-y-2">
                {showAllSov
                  ? sov.map(renderSovRow)
                  : (
                    <>
                      {sovCollapsed.head.map(renderSovRow)}
                      {sovCollapsed.pinned && (
                        <>
                          <div className="px-0.5 text-[11.5px] text-slate-400">… {sovCollapsed.gap} more</div>
                          {renderSovRow(sovCollapsed.pinned)}
                        </>
                      )}
                    </>
                  )}
              </div>
              {sov.length > SOV_LIMIT && (
                <ShowMoreButton expanded={showAllSov} count={sov.length} noun="brands" onClick={() => setShowAllSov((v) => !v)} />
              )}
              <p className="text-[11.5px] text-slate-400">
                From neutral buyer queries (no brand named) — who the model recommends on its own.{" "}
                <Link to="/app/blind" className="text-brand-600 font-medium hover:underline">All blind queries →</Link>
              </p>
            </CardContent>
          </Card>
        )}

        {/* What changed — run diffs, alerts, and new brands merged (see /api/feed) */}
        {feedItems.length > 0 && (
          <Card className={cn(sov.length === 0 && "lg:col-span-2")}>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">What changed this week</span>
                <Badge variant="category">{feedItems.length} signal{feedItems.length === 1 ? "" : "s"}</Badge>
              </div>
              <div>
                {feedItems.slice(0, 6).map((item, i) => {
                  const hasTopic = !!item.topic && item.topic !== "general";
                  const pillLabel = hasTopic ? item.topic!.charAt(0).toUpperCase() + item.topic!.slice(1) : "Signal";
                  const pillClass = cn(
                    "shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold",
                    hasTopic ? "bg-brand-50 text-brand-600" : "bg-slate-50 text-slate-500 border border-slate-200",
                  );
                  return (
                    <div key={i} className="flex items-baseline gap-3 py-2.5 border-t border-slate-100 first:border-t-0 text-[12.5px] text-slate-700">
                      {item.href ? (
                        <Link to={item.href} className={cn(pillClass, "hover:opacity-80")}>{pillLabel}</Link>
                      ) : (
                        <span className={pillClass}>{pillLabel}</span>
                      )}
                      <span className="min-w-0 flex-1">
                        {item.href ? (
                          <Link to={item.href} className="hover:text-brand-600">{item.message}</Link>
                        ) : (
                          item.message
                        )}
                      </span>
                      <span className="text-[11.5px] text-slate-400 shrink-0 tabular-nums">{fmtFeedDate(item.occurred_at)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Objectivity check */}
      <div className="grid">
        <Card>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-brand-500" />
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">
                {pinnedLens ? "Objectivity check — same lens, every brand" : "Field ranking — all recorded runs"}
              </span>
              {pinnedLens && <Badge variant="category">Same lens</Badge>}
            </div>
            <div className="space-y-1">
              {showAllField
                ? data.field.map(renderFieldRow)
                : (
                  <>
                    {fieldCollapsed.head.map(renderFieldRow)}
                    {fieldCollapsed.pinned && (
                      <>
                        <div className="px-2.5 text-[11.5px] text-slate-400">… {fieldCollapsed.gap} more</div>
                        {renderFieldRow(fieldCollapsed.pinned)}
                      </>
                    )}
                  </>
                )}
              {data.field.length > FIELD_LIMIT && (
                <ShowMoreButton expanded={showAllField} count={data.field.length} noun="brands" onClick={() => setShowAllField((v) => !v)} />
              )}
            </div>
            <p className="text-[11.5px] text-slate-400">
              {pinnedSingle ? (
                <>Every brand — yours and each competitor — is scored with the same “{data.lens.prompt_name ?? "tracking"}” prompt in the same scheduled runs, so the ranking is apples-to-apples.</>
              ) : pinnedLens ? (
                <>Every brand — yours and each competitor — is scored through your tracked topics' prompts: the same questions, every brand, every week, so the ranking is apples-to-apples.</>
              ) : (
                <>Ranked across all runs recorded in this workspace over the window — different prompts and models may be mixed. Set up topic tracking to score every brand through one shared lens.</>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {reports.length > 0 && (
        <Card>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <Share2 className="w-4 h-4 text-brand-500" />
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Shared reports</span>
            </div>
            {reports.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-1">
                <a
                  href={`/snapshots/${r.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 min-w-0 text-[13px] font-medium text-brand-600 hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{r.name}</span>
                </a>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[11.5px] text-slate-400">{r.period_label}</span>
                  <Button variant="ghost" size="icon" onClick={() => deleteReport(r.id)} aria-label="Delete report" className="text-slate-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            <p className="text-[11.5px] text-slate-400 pt-1">Frozen, public links — anyone with the URL can view. Immutable once created.</p>
          </CardContent>
        </Card>
      )}

      <p className="text-[11.5px] text-slate-400 text-center pt-1">
        Sentiment is 0–100 (50 = neutral). Figures update as scheduled studies record new runs.
      </p>
    </div>
  );
}
